import { useContext, useEffect, useState } from "react";
import Tabbar from "../../../../components/shared/tabbar/tabbar";
import { TabbarOption } from "../../../../components/shared/tabbar/types/tabbarProps";
import { CommentsSidebarProps, CommentsTabs } from "./types/commentsSidebarProps";
import './commentsSidebar.scss';
import ButtonField from "../../../../components/buttonField/buttonField";
import { classNameBtn, iconPosition } from "../../../../components/shared/button/types/buttonProps";
import { stylizationVerticalAlign } from "../../../../components/shared/stylization";
import { ButtonFieldProps } from "../../../../components/buttonField/types/buttonFieldProps";
import { ReactComponent as close } from '../../../../icons/close.svg';
import CommentCard from "./commentCard/commentCard";
import KrFormContext, { FormComment } from "../../../../contexts/krFormContext";
import TextAreaField from "../../../../components/textAreaField/textAreaField";
import { TextAreaFieldProps } from "../../../../components/textAreaField/types/textAreaFieldProps";
import AuthContext from "../../../../contexts/authContext";
import { notifyError } from "../../../../components/snackbar/snackbar";
import { KROFieldDict, KRTZFieldDict, getHeadingByFieldDict } from "./fieldDict";

const CommentsSidebar = (props: CommentsSidebarProps) => {
    const { username } = useContext(AuthContext)
    const { krForm, setkrForm } = useContext(KrFormContext);
    const [selectedTab, setSelectedTab] = useState<CommentsTabs>(CommentsTabs.AllComments);
    const [primaryComments, setPrimaryComments] = useState<FormComment[]>([]);
    const [newCommentContent, setNewCommentContent] = useState<string>("");
    const [disableBtn, setDisableBtn] = useState<boolean>(true);
    const [addCommentFieldVisible, setAddCommentFieldVisible] = useState<boolean>(false);
    const [filterForNotCorrected, setFilterForNotCorrected] = useState<boolean>(false);
    const [filterByField, setFilterByField] = useState<string | undefined>(props.filterByField);

    // Tabbar
    var Options: TabbarOption[] = [
        {
            name: 'Wszystkie komentarze', onClick: () => {
                setSelectedTab(CommentsTabs.AllComments)
            }
        },
        {
            name: 'Moje komentarze', onClick: () => {
                setSelectedTab(CommentsTabs.MyComments)
            }
        }
    ]

    // Filter buttons
    const getBtnProps = (title: string, btnClass: string, onClick: React.MouseEventHandler<HTMLButtonElement>, height: number, disable: boolean,
        icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>): ButtonFieldProps => {
        return {
            BtnProps: {
                onClick: onClick,
                className: `${btnClass} filterBtn body-txt-medium`,
                style: {
                    height: `${height}px`
                },
                disabled: disable
            },
            placeHolder: title,
            placeHolderStyle: stylizationVerticalAlign,
            additionalIcon: icon,
            iconPosition: iconPosition.Start,
            wrapperProps: {
                style: {
                    width: "auto",
                }
            }
        };
    }

    const handleNotCorrectedFilterClick = () => {
        setFilterForNotCorrected(!filterForNotCorrected);
    }

    const handleClearFiltersClick = () => {
        setFilterByField(undefined);
        setFilterForNotCorrected(false);
        if (props.setClearCommentsFilterHighlight) props.setClearCommentsFilterHighlight();
    }

    // Add comment
    useEffect(() => {
        if (props.newCommentFieldDetails?.visible) {
            if (krForm.formComments && krForm.formComments.some(item => item.atrybuT_KOMENTOWANY === props.newCommentFieldDetails?.fieldDictName)) {
                notifyError({ message: "Do wybranego pola dodano już komentarz" });
            }
            else setAddCommentFieldVisible(true);
            setDisableBtn(true)
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.newCommentFieldDetails]);

    var textareaProps: TextAreaFieldProps = {
        textAreaPlaceholder: "Wpisz komentarz",
        type: "text",
        keepPristine: true,
        setValue: (value: any) => {
            setNewCommentContent(value);
        },
        onFill: (isFill: boolean) => {
            setDisableBtn(!isFill);
        },
        textareaSharedProps: {
            style: {
                height: "200px"
            }
        }
    }

    const handleAddNewCommentClick = () => {
        var newComment: FormComment = {
            poczekalniA_ID: krForm.waitingRoomId,
            atrybuT_KOMENTOWANY: props.newCommentFieldDetails?.fieldDictName,
            datA_KOMENTARZA: new Date().getTime(),
            tresc: newCommentContent,
            autor: username,
            czY_POPRAWIONE: 0,
            komentarZ_GID: null,
            localUUID: crypto.randomUUID(),
            sectionHeading: getHeadingByFieldDict(props.newCommentFieldDetails?.fieldDictName!, props.isLandslide!, props.is2007!)
        }
        setAddCommentFieldVisible(false);

        var form: any = Object.assign({}, krForm);
        if (!form.formComments) {
            form.formComments = [];
        }
        var newComments: FormComment[] = [...form.formComments, newComment]
        form.formComments = newComments;
        setkrForm(form);
    }

    const handleCancelNewCommentClick = () => {
        setAddCommentFieldVisible(false);
        if (props.setNewCommentFieldDetails) props.setNewCommentFieldDetails(undefined);
    }

    // Comments
    useEffect(() => {
        setPrimaryComments(getPrimaryComments());

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [krForm.formComments, filterByField, selectedTab, filterForNotCorrected])

    useEffect(() => {
        setFilterByField(props.filterByField);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.filterByField])

    const getPrimaryComments = () => {
        var primaryCommentsList: FormComment[] | undefined = [];

        if (selectedTab === CommentsTabs.AllComments) primaryCommentsList = krForm.formComments?.filter(item => item.komentarZ_GID === null);
        else if (props.editMode) primaryCommentsList = krForm.formComments?.filter(item => item.comments?.some(elem => elem.autor === username));
        else primaryCommentsList = krForm.formComments?.filter(item => item.komentarZ_GID === null && item.autor === username);

        if (filterForNotCorrected) primaryCommentsList = primaryCommentsList?.filter(item => item.czY_POPRAWIONE === 0);

        if (primaryCommentsList && filterByField && filterByField !== null) {
            primaryCommentsList = primaryCommentsList.filter(item => item.atrybuT_KOMENTOWANY === filterByField);
        }

        primaryCommentsList = sortPrimaryCommentsByField(primaryCommentsList);

        return primaryCommentsList && primaryCommentsList?.length > 0 ? primaryCommentsList : [];
    }

    const sortPrimaryCommentsByField = (comments: FormComment[] | undefined) => {
        if (comments) {
            const fieldOrder2 = props.isLandslide ? Object.keys(KROFieldDict) : Object.keys(KRTZFieldDict);

            var sortedComments = comments.sort((a, b) => {
                const ind1 = fieldOrder2.findIndex(key => a.atrybuT_KOMENTOWANY!.includes(key.toString()));
                const ind2 = fieldOrder2.findIndex(key => b.atrybuT_KOMENTOWANY!.includes(key.toString()));
                return ind1 - ind2
            })

            return sortedComments;
        }
    }

    const getSecondaryComments = (primaryComment: FormComment) => {
        var secondaryComments = krForm.formComments?.filter(item => item.komentarZ_GID === primaryComment.globalid);
        return secondaryComments && secondaryComments?.length > 0 ? secondaryComments : [];
    }

    const checkIfFirstCommentForSection = (primaryComment: FormComment) => {
        var firstCommentForSection = primaryComments.find((item: FormComment) => item.sectionHeading === primaryComment.sectionHeading);
        return firstCommentForSection?.atrybuT_KOMENTOWANY === primaryComment.atrybuT_KOMENTOWANY;
    }

    return (
        <>
            <div className="commentsTabbarWrapper">
                <Tabbar selected={selectedTab + 1} options={Options}></Tabbar>
            </div>
            <div className="commentsFilterBtns">
                {props.editMode &&
                    <ButtonField {...getBtnProps("Tylko niepoprawione", filterForNotCorrected ? classNameBtn.Small : classNameBtn.SmallSecondary, handleNotCorrectedFilterClick, 50, false)}></ButtonField>
                }
                <ButtonField {...getBtnProps("Wyczyść filtrowanie", classNameBtn.Small, handleClearFiltersClick, 50, false, close)}></ButtonField>
            </div>
            {addCommentFieldVisible &&
                <div className="addNewComment">
                    <h4 style={{ fontWeight: "600" }}>{props.newCommentFieldDetails?.fieldFormName}</h4>
                    <TextAreaField {...textareaProps} />
                    <div className="addCommentBtn">
                        <ButtonField {...getBtnProps("Anuluj", classNameBtn.SmallSecondary, handleCancelNewCommentClick, 40, false)}></ButtonField>
                        <ButtonField {...getBtnProps("Dodaj komentarz", classNameBtn.Small, handleAddNewCommentClick, 40, disableBtn)}></ButtonField>
                    </div>
                </div>
            }
            <div>
                {primaryComments.map(item => {
                    item.comments = getSecondaryComments(item);
                    return (
                        <CommentCard
                            key={item.localUUID}
                            primaryComment={item}
                            editMode={props.editMode}
                            executeScroll={props.executeScroll}
                            isLandslide={props.isLandslide}
                            is2007={props.is2007}
                            isFirstForSection={checkIfFirstCommentForSection(item)}
                        />
                    )
                })}
            </div>
        </>
    );

}

export default CommentsSidebar;